import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { getEnumValueByKeySafe } from "components/utils";
import CommunicationValue from "entities/CommunicationValue";
import moment from "moment";
import CommunicationValueType from "util/config/communicationValueType";
import AdditionalPersonData from "../../entities/AdditionalPersonData";

import i18n from "../../i18n";
import { default as Gender } from "../../util/config/gender";
import {
    default as recordStatus,
    default as RecordStatus
} from "../../util/config/recordStatus";
import RelationshipType from "../../util/config/relationshipType";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader, getWorkerId } from "../users/auth";
import RelatedPerson from "../../entities/relatedPersons/RelatedPerson";
import RelatedPersonRequest from "../../entities/relatedPersons/RelatedPersonRequest";
import { extractCollection } from "./normalizers";

// RESPONSE DECLARATIONS //
interface IdentificationNumberResponse {
    Value: string;
}

interface RelatedPersonResponse extends ResponseBase {
    AdditionalData: string;
    BirthDate?: string;
    CommunicationValues?: CommunicationValueResponse[];
    CreatedAt?: string;
    Email: string;
    Gender?: keyof typeof Gender;
    IdentificationNumbers: IdentificationNumberResponse[];
    IsDependent: boolean;
    IsEmergencyContact: boolean;
    ModifiedAt?: string;
    Name: string;
    FirstName: string;
    LastName: string;
    Phone: string;
    RecordStatus: keyof typeof RecordStatus;
    RelationshipType: keyof typeof RelationshipType;
    WorkerId: number;
    WorkerName: string;
}

interface CommunicationValueResponse {
    Id: number;
    PersonId: number;
    BelongsTo: number;
    TypeId: number;
    Value: string;
    ValueType: keyof typeof CommunicationValueType;
    StartDate?: string;
    EndDate?: string | null;
    RecordStatus: keyof typeof RecordStatus;
}

// SLICE //
interface State extends SliceCollectionBase<RelatedPerson> {
    byType: SliceCollectionBase<RelatedPerson>;
    contactPersons: SliceCollectionBase<RelatedPerson>;
    workerRelatedPersons: SliceCollectionBase<RelatedPerson>;

    reloadData: boolean;
    editContactVisible: boolean;
    editContactId: number;
    editChildVisible: boolean;
    editChildId: number;
    additionalDataStructure: AdditionalPersonData | null;
    fetchedChildren: boolean;
}

const initialState: State = {
    list: [],
    loading: false,
    lastFetch: null,
    error: null,
    byType: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    contactPersons: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    workerRelatedPersons: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },

    reloadData: false,
    editContactVisible: false,
    editContactId: -1,
    editChildVisible: false,
    editChildId: -1,
    additionalDataStructure: null,
    fetchedChildren: false
};

const slice = createSlice({
    name: "relatedPersons",
    initialState,
    reducers: {
        // RELATED PERSONS
        relatedPersonsRequested: relatedPersons => {
            relatedPersons.loading = true;
            relatedPersons.error = null;
        },
        relatedPersonsReceived: (
            relatedPersons,
            action: PayloadAction<unknown>
        ) => {
            relatedPersons.list = schema(extractCollection<RelatedPersonResponse>(action.payload));
            relatedPersons.loading = false;
            relatedPersons.lastFetch = Date.now();
            relatedPersons.fetchedChildren = true;
        },
        relatedPersonsRequestFailed: (
            relatedPersons,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            relatedPersons.loading = false;
            relatedPersons.error = action.payload;
            relatedPersons.fetchedChildren = true;
        },

        // RELATED PERSONS BY TYPE
        relatedPersonsByTypeRequested: relatedPersons => {
            relatedPersons.byType.loading = true;
        },
        relatedPersonsByTypeReceived: (
            relatedPersons,
            action: PayloadAction<RelatedPersonResponse[]>
        ) => {
            relatedPersons.byType.list = schema(action.payload);
            relatedPersons.byType.loading = false;
            relatedPersons.byType.lastFetch = Date.now();
        },
        relatedPersonsByTypeRequestFailed: (
            relatedPersons,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            relatedPersons.byType.loading = false;
            relatedPersons.byType.error = action.payload;
        },

        // WORKER CONTACT PERSONS
        contactPersonsRequested: relatedPersons => {
            relatedPersons.contactPersons.loading = true;
            relatedPersons.contactPersons.error = null;
        },
        contactPersonsReceived: (
            relatedPersons,
            action: PayloadAction<RelatedPersonResponse[]>
        ) => {
            const payload = action.payload;
            const relatedPersonsContacts = [];

            //TODO: MAYBE IN SCHEMA?
            if (Array.isArray(payload)) {
                for (let relatedPerson of payload) {
                    if (typeof relatedPerson.AdditionalData === "string") {
                        try {
                            relatedPerson.AdditionalData = JSON.parse(
                                relatedPerson.AdditionalData
                            );
                        } catch (error) {
                            console.error(
                                "Failed to parse AdditionalData:",
                                relatedPerson.AdditionalData
                            );
                        }
                    }
                    relatedPersonsContacts.push(relatedPerson);
                }
            }

            relatedPersons.contactPersons.list = schema(relatedPersonsContacts);
            relatedPersons.contactPersons.loading = false;
            relatedPersons.contactPersons.lastFetch = Date.now();
        },
        contactPersonsRequestFailed: (
            relatedPersons,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            relatedPersons.contactPersons.loading = false;
            relatedPersons.contactPersons.error = action.payload;
        },

        //
        workerRelatedPersonsRequested: relatedPersons => {
            relatedPersons.workerRelatedPersons.loading = true;
            relatedPersons.workerRelatedPersons.error = null;
        },
        workerRelatedPersonsReceived: (
            relatedPersons,
            action: PayloadAction<RelatedPersonResponse[]>
        ) => {
            const payload = action.payload;
            const relatedPersonsContacts = [];

            //TODO: MAYBE IN SCHEMA?
            if (Array.isArray(payload)) {
                for (let relatedPerson of payload) {
                    if (typeof relatedPerson.AdditionalData === "string") {
                        try {
                            relatedPerson.AdditionalData = JSON.parse(
                                relatedPerson.AdditionalData
                            );
                        } catch (error) {
                            console.error(
                                "Failed to parse AdditionalData:",
                                relatedPerson.AdditionalData
                            );
                        }
                    }
                    relatedPersonsContacts.push(relatedPerson);
                }
            }

            relatedPersons.workerRelatedPersons.list = schema(relatedPersonsContacts);
            relatedPersons.workerRelatedPersons.loading = false;
            relatedPersons.workerRelatedPersons.lastFetch = Date.now();
        },
        workerRelatedPersonsRequestFailed: (
            relatedPersons,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            relatedPersons.workerRelatedPersons.loading = false;
            relatedPersons.workerRelatedPersons.error = action.payload;
        },


        relatedPersonsChangedStarted: relatedPersons => {
            relatedPersons.reloadData = true;
            relatedPersons.editContactId = -1;
            relatedPersons.editChildId = -1;
            relatedPersons.editContactVisible = false;
            relatedPersons.editChildVisible = false;
        },
        relatedPersonsChangedCompleted: relatedPersons => {
            relatedPersons.reloadData = false;
            relatedPersons.editContactId = -1;
            relatedPersons.editChildId = -1;
            relatedPersons.editContactVisible = false;
            relatedPersons.editChildVisible = false;
        },
       
        relatedPersonsChangedFailed: (
            relatedPersons,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            relatedPersons.reloadData = false;
/*            relatedPersons.workerRelatedPersons.error = action.payload;*/
        },
        //
        showEditContactModal: (relatedPersons, action: PayloadAction<number>) => {
            relatedPersons.editContactId = action.payload;
            relatedPersons.editContactVisible = true;
        },

        hideEditContactModal: relatedPersons => {
            relatedPersons.editContactVisible = false;
            relatedPersons.editContactId = -1;
        },

        //
        showEditChildModal: (relatedPersons, action: PayloadAction<number>) => {
            relatedPersons.editChildId = action.payload;
            relatedPersons.editChildVisible = true;
        },

        hideEditChildModal: relatedPersons => {
            relatedPersons.editChildVisible = false;
            relatedPersons.editChildId = -1;
        },

        relatedPersonDeletedStarted: relatedPersons => {
            relatedPersons.reloadData = true;
        },
        relatedPersonDeletedCompleted: relatedPersons => {
            relatedPersons.reloadData = false;
        },
        relatedPersonDeletedFailed: relatedPersons => {
            relatedPersons.reloadData = false;
        },

        // ADD ADDITIONAL DATA STRUCTURE
        additionalDataStructureAdded: (
            relatedPersons,
            action: PayloadAction<AdditionalPersonData>
        ) => {
            relatedPersons.additionalDataStructure = action.payload;
        }
    }
});

const {
    relatedPersonsRequested,
    relatedPersonsReceived,
    relatedPersonsRequestFailed,

    relatedPersonsByTypeRequested,
    relatedPersonsByTypeReceived,
    relatedPersonsByTypeRequestFailed,

    contactPersonsRequested,
    contactPersonsReceived,
    contactPersonsRequestFailed,

    workerRelatedPersonsRequested,
    workerRelatedPersonsReceived,
    workerRelatedPersonsRequestFailed,

    relatedPersonsChangedStarted,
    relatedPersonsChangedCompleted,
    relatedPersonsChangedFailed,

    showEditContactModal,
    hideEditContactModal,

    showEditChildModal,
    hideEditChildModal,

    relatedPersonDeletedCompleted,
    relatedPersonDeletedStarted,
    relatedPersonDeletedFailed,

    additionalDataStructureAdded
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
// TODO: REPLACE WITH API METHOD REQUEST
export const getRelatedPersons =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        const { loading } = getState().entities.relatedPersons;
        if (loading) {
            return;
        }

        const workerId = getWorkerId(getState());
        const url = `/api/classificator/persons?belongsTo=${workerId}&orderBy=BirthDate`;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: relatedPersonsRequested.type,
                onSuccess: relatedPersonsReceived.type,
                onError: relatedPersonsRequestFailed.type
            })
        );
    };

/**
 * As of 07.2024 this function is accessible only for HR role.
 *
 */
export const getRelatedPersonsByTypes =
    (relationshipTypes: (RelationshipType | string)[]) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.relatedPersons.byType.loading) {
                return;
            }
            try {
                const requests = relationshipTypes.map(type => {
                    const url = `/api/RelatedPersons/bytype?type=${encodeURIComponent(type)}`;
                    return dispatch(
                        apiCallBegan({
                            url,
                            method: "GET",
                            headers: { ...authHeader(), "Accept-Language": i18n.language },
                            onStart: relatedPersonsByTypeRequested.type,
                            onSuccess: relatedPersonsByTypeReceived.type,
                            onError: relatedPersonsByTypeRequestFailed.type
                        })
                    );
                });
                const results = await Promise.all(requests);

                const combinedResults = results.flat();
                return combinedResults;
            } catch (error) {
                console.error(
                    "Failed to fetch related persons by multiple types:",
                    error
                );
                throw error;
            }
        };

//For HR
export const getContactPersons =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.relatedPersons.contactPersons.loading) {
            return;
        }

         const workerId = getWorkerId(getState());

        const url = `/api/RelatedPersons/contacts/${workerId}`;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: contactPersonsRequested.type,
                onSuccess: contactPersonsReceived.type,
                onError: contactPersonsRequestFailed.type
            })
        );
    };

export const getWorkerRelatedPersons =
    (includeChildren: boolean) => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.relatedPersons.workerRelatedPersons.loading) {
            return;
        }
        const workerId = getWorkerId(getState());

        const url = `/api/relatedpersons/by-worker/${workerId}?includeChildren=${!!includeChildren}`;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: workerRelatedPersonsRequested.type,
                onSuccess: workerRelatedPersonsReceived.type,
                onError: workerRelatedPersonsRequestFailed.type
            })
        );
    };

export const saveContactPerson =
    (contactPerson: RelatedPersonRequest) => async (dispatch: AppDispatch, getState: () => AppState) => {
         const workerId = getWorkerId(getState());
        await dispatch(
            apiCallBegan({
                url: "/api/RelatedPersons/" + workerId,
                method: contactPerson.Id ? "patch" : "post",
                headers: {
                    ...authHeader(),
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(contactPerson),
                onStart: relatedPersonsChangedStarted.type,
                onSuccess: relatedPersonsChangedCompleted.type,
                onError: relatedPersonsChangedFailed.type
            })
        );
    };

export const deleteRelatedPerson =
    (personId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
        const workerId = getWorkerId(getState());
        await dispatch(
            apiCallBegan({
                url: `/api/RelatedPersons/${personId}/${workerId}`,
                method: "delete",
                headers: {
                    ...authHeader(),
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                onStart: relatedPersonDeletedStarted.type,
                onSuccess: relatedPersonDeletedCompleted.type,
                onError: relatedPersonDeletedFailed.type
            })
        );
    };

export const editContact =
    (editPersonId?: number) => async (dispatch: AppDispatch) => {
        dispatch({
            type: showEditContactModal.type,
            payload: editPersonId
        });
    };

export const hideEditContact = () => async (dispatch: AppDispatch) => {
    dispatch({
        type: hideEditContactModal.type
    });
};


export const hideEditChild = () => async (dispatch: AppDispatch) => {
    dispatch({
        type: hideEditChildModal.type
    });
};

// SELECTORS //


export const getContactPersonsData = createSelector(
    state => state.entities.relatedPersons.list,
    (relatedPersons: RelatedPerson[]) =>
        relatedPersons.filter(
            relatedPerson =>
                relatedPerson.RecordStatus !== recordStatus.Deleted &&
                relatedPerson.IsEmergencyContact,
            relatedPersons.some(
                //@ts-ignore - TODO: needs to be corrected later //Ada: But... what needs to be corrected?
                name => name.EndDate === null || new Date(name.EndDate) <= new Date()
            )
        )
);

export const childRelationshipTypes = [
    RelationshipType.Child,
    RelationshipType.Daughter,
    RelationshipType.Son
];

const mapCommunicationValues = (
    values: CommunicationValueResponse[]
): CommunicationValue[] => {
    if (!values || values.length === 0) {
        return [];
    }

    return values.map(value => ({
        Id: value.Id,
        PersonId: value.PersonId,
        Code: "", // Placeholder if Code is needed elsewhere
        Name: "", // Placeholder if Name is needed elsewhere
        StartDate: value.StartDate ? new Date(value.StartDate).toString() : undefined,
        EndDate: value.EndDate ? new Date(value.EndDate).toString() : undefined,
        TypeId: value.TypeId,
        Value: value.Value,
        ValueType:
            CommunicationValueType[
            value.ValueType as keyof typeof CommunicationValueType
            ]
    }));
};

// NORMALIZATION //
const schema = (payload: RelatedPersonResponse[]): RelatedPerson[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map((resp: RelatedPersonResponse): RelatedPerson => {
        const gender = getEnumValueByKeySafe(Gender, resp.Gender) ?? Gender.Unknown;
        const relationshipType =
            getEnumValueByKeySafe(RelationshipType, resp.RelationshipType) ??
            RelationshipType.Other;
       // console.log("resp", resp);

        // Generate dynamic translation for 'Parent'
        let relationshipTypeTranslation = i18n.t(
            "common:RelationshipType." + resp.RelationshipType
        );
        if (relationshipType === RelationshipType.Parent) {
            switch (gender) {
                case Gender.Male:
                    relationshipTypeTranslation = i18n.t(
                        "common:RelationshipType.Father"
                    );
                    break;
                case Gender.Female:
                    relationshipTypeTranslation = i18n.t(
                        "common:RelationshipType.Mother"
                    );
                    break;
                default:
                    relationshipTypeTranslation = i18n.t(
                        "common:RelationshipType.Parent"
                    );
                    break;
            }
        }

        return {
            AdditionalData:
                typeof resp.AdditionalData === "string"
                    ? JSON.parse(resp.AdditionalData)
                    : resp.AdditionalData,
            BirthDate: resp.BirthDate?.substring(0, 10),
            CommunicationValues: mapCommunicationValues(resp.CommunicationValues || []),
            CreatedAt: resp.CreatedAt,
            Email: resp.Email,
            Gender: gender,
            Id: resp.Id,
            IdentificationNumber: Array.isArray(resp.IdentificationNumbers) ? resp.IdentificationNumbers[0]?.Value : "",
            IsDependent: resp.IsDependent,
            IsEmergencyContact: resp.IsEmergencyContact,
            ModifiedAt: resp.ModifiedAt,
            Name: resp.Name,
            FirstName: resp.FirstName,
            LastName:resp.LastName,
            Phone: resp.Phone,
            RecordStatus: getEnumValueByKeySafe(RecordStatus, resp.RecordStatus),
         
           
            WorkerId: resp.WorkerId,
            WorkerName: resp.WorkerName
        };
    });
};
